if (!customElements.get('product-form')) {
  customElements.define(
    'product-form',
    class ProductForm extends HTMLElement {
      constructor() {
        super();

        this.form = this.querySelector('form');
        this.submitButton = this.querySelector('[type="submit"]');
        this.submitButtonText =
          this.submitButton?.querySelector('span');

        this.cart =
          document.querySelector('cart-notification') ||
          document.querySelector('cart-drawer');

        this.hideErrors =
          this.dataset.hideErrors === 'true';

        if (!this.form || !this.submitButton) return;

        const variantInput = this.variantIdInput;

        if (variantInput) {
          variantInput.disabled = false;
        }

        this.form.addEventListener(
          'submit',
          this.onSubmitHandler.bind(this)
        );

        if (document.querySelector('cart-drawer')) {
          this.submitButton.setAttribute(
            'aria-haspopup',
            'dialog'
          );
        }
      }

      async onSubmitHandler(event) {
        event.preventDefault();

        if (
          !this.submitButton ||
          this.submitButton.getAttribute('aria-disabled') === 'true'
        ) {
          return;
        }

        this.handleErrorMessage();
        this.setLoadingState(true);

        const config = fetchConfig('javascript');

        config.headers['X-Requested-With'] =
          'XMLHttpRequest';

        delete config.headers['Content-Type'];

        const formData = new FormData(this.form);

        /*
         * Request Dawn cart sections when a drawer exists.
         * A failure to render those sections must never be
         * treated as an Add to Cart failure.
         */
        if (
          this.cart &&
          typeof this.cart.getSectionsToRender === 'function'
        ) {
          const sectionIds = this.cart
            .getSectionsToRender()
            .map((section) => section.id);

          formData.append(
            'sections',
            sectionIds.join(',')
          );

          formData.append(
            'sections_url',
            window.location.pathname
          );

          if (
            typeof this.cart.setActiveElement === 'function'
          ) {
            this.cart.setActiveElement(
              document.activeElement
            );
          }
        }

        config.body = formData;

        try {
          const response = await fetch(
            window.routes.cart_add_url,
            config
          );

          const responseData = await response.json();

          /*
           * Genuine Shopify cart error:
           * sold out, invalid quantity, unavailable variant, etc.
           */
          if (!response.ok || responseData.status) {
            const errorMessage =
              responseData.description ||
              responseData.message ||
              'Unable to add this item. Please try again.';

            this.handleErrorMessage(errorMessage);

            if (
              typeof publish === 'function' &&
              window.PUB_SUB_EVENTS
            ) {
              publish(PUB_SUB_EVENTS.cartError, {
                source: 'product-form',
                productVariantId: formData.get('id'),
                errors:
                  responseData.errors ||
                  responseData.description,
                message: responseData.message
              });
            }

            this.error = true;
            return;
          }

          /*
           * The item is now successfully inside Shopify cart.
           * Never show an Add to Cart error after this point.
           */
          this.error = false;

          if (
            typeof publish === 'function' &&
            window.PUB_SUB_EVENTS
          ) {
            try {
              publish(PUB_SUB_EVENTS.cartUpdate, {
                source: 'product-form',
                productVariantId: formData.get('id'),
                cartData: responseData
              });
            } catch (publishError) {
              console.warn(
                'Cart event could not be published:',
                publishError
              );
            }
          }

          /*
           * Update cart drawer using Dawn's returned sections.
           * Keep this isolated so a drawer error cannot create
           * a false Add to Cart failure message.
           */
          if (
            this.cart &&
            typeof this.cart.renderContents === 'function'
          ) {
            try {
              this.cart.renderContents(responseData);
            } catch (drawerError) {
              console.warn(
                'Item added, but cart drawer refresh failed:',
                drawerError
              );
            }
          }

          /*
           * Always fetch the actual Shopify cart and update
           * the cart-count bubble independently.
           */
          await this.refreshCartCount();

        } catch (requestError) {
          /*
           * This block runs only when the actual cart request
           * fails before Shopify confirms the item addition.
           */
          console.error(
            'Actual Add to Cart request failed:',
            requestError
          );

          this.handleErrorMessage(
            'Unable to add this item. Please check your connection and try again.'
          );

          this.error = true;
        } finally {
          this.setLoadingState(false);
        }
      }

      async refreshCartCount() {
        try {
          const response = await fetch(
            `${window.routes.cart_url}.js`,
            {
              method: 'GET',
              headers: {
                Accept: 'application/json'
              },
              cache: 'no-store'
            }
          );

          if (!response.ok) {
            throw new Error(
              `Cart refresh failed with status ${response.status}`
            );
          }

          const cart = await response.json();

          this.updateCartBubble(cart.item_count);

          const cartDrawer =
            document.querySelector('cart-drawer');

          if (
            cartDrawer &&
            cartDrawer.classList.contains('is-empty') &&
            cart.item_count > 0
          ) {
            cartDrawer.classList.remove('is-empty');
          }
        } catch (error) {
          console.warn(
            'The item was added, but the cart number could not refresh:',
            error
          );
        }
      }

      updateCartBubble(itemCount) {
        const cartIcon =
          document.getElementById('cart-icon-bubble');

        if (!cartIcon) {
          console.warn(
            'Header element #cart-icon-bubble was not found.'
          );
          return;
        }

        let bubble =
          cartIcon.querySelector('.cart-count-bubble');

        if (itemCount <= 0) {
          bubble?.remove();
          return;
        }

        if (!bubble) {
          bubble = document.createElement('div');
          bubble.className = 'cart-count-bubble';

          bubble.innerHTML = `
            <span aria-hidden="true"></span>
            <span class="visually-hidden"></span>
          `;

          cartIcon.appendChild(bubble);
        }

        const visibleNumber =
          bubble.querySelector(
            'span[aria-hidden="true"]'
          );

        const accessibleNumber =
          bubble.querySelector(
            '.visually-hidden'
          );

        if (visibleNumber) {
          visibleNumber.textContent =
            itemCount < 100 ? itemCount : '99+';
        }

        if (accessibleNumber) {
          accessibleNumber.textContent =
            `${itemCount} item${itemCount === 1 ? '' : 's'} in cart`;
        }
      }

      setLoadingState(isLoading) {
        if (!this.submitButton) return;

        const spinner =
          this.querySelector('.loading__spinner');

        if (isLoading) {
          this.submitButton.setAttribute(
            'aria-disabled',
            'true'
          );

          this.submitButton.classList.add('loading');
          spinner?.classList.remove('hidden');
        } else {
          this.submitButton.classList.remove('loading');
          spinner?.classList.add('hidden');

          if (!this.error) {
            this.submitButton.removeAttribute(
              'aria-disabled'
            );
          }
        }
      }

      handleErrorMessage(errorMessage = false) {
        if (this.hideErrors) return;

        this.errorMessageWrapper =
          this.errorMessageWrapper ||
          this.querySelector(
            '.product-form__error-message-wrapper'
          );

        if (!this.errorMessageWrapper) return;

        this.errorMessage =
          this.errorMessage ||
          this.errorMessageWrapper.querySelector(
            '.product-form__error-message'
          );

        this.errorMessageWrapper.toggleAttribute(
          'hidden',
          !errorMessage
        );

        if (
          errorMessage &&
          this.errorMessage
        ) {
          this.errorMessage.textContent =
            errorMessage;
        }
      }

      toggleSubmitButton(disable = true, text) {
        if (!this.submitButton) return;

        if (disable) {
          this.submitButton.setAttribute(
            'disabled',
            'disabled'
          );

          if (
            text &&
            this.submitButtonText
          ) {
            this.submitButtonText.textContent =
              text;
          }
        } else {
          this.submitButton.removeAttribute(
            'disabled'
          );

          if (this.submitButtonText) {
            this.submitButtonText.textContent =
              window.variantStrings.addToCart;
          }
        }
      }

      get variantIdInput() {
        return this.form?.querySelector(
          '[name="id"]'
        );
      }
    }
  );
}